// apps/web/src/contexts/UserContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { apiGet } from "../utils/apiClient";
import {
    getCompanyId,
    setCompanyId as saveCompanyId,
    getSelectedCompanyId,
    setSelectedCompanyId,
    clearCompanyContext,
} from "../utils/companyStorage";

export type AdminCompany = {
    tenantId?: string | null;
    companyId: string;
    shopUrl: string;
    plan?: string | null;
    trialStatus?: string | null;
    role?: string | null;
    status?: string | null;
};

export type UserState = {
    uid: string | null;
    tenantId: string | null;
    companyId: string | null;
    role: string | null;
    shopUrl: string | null;
    plan: string | null;
    trialStatus: string | null;
    superAdmin: boolean;
    superAdminGlobal: boolean;
    superAdminTenants: string[];
    adminCompanies: AdminCompany[];
    loading: boolean;
    status: "pending" | "approved" | null;
    error: "tenant_mismatch" | "unauthorized" | null;
    setCompanyId: (id: string | null) => void;
    applySelectedCompany: (payload: {
        tenantId?: string | null;
        companyId?: string | null;
        shopUrl?: string | null;
        plan?: string | null;
        trialStatus?: string | null;
    }) => void;
};

const defaultState: UserState = {
    uid: null,
    tenantId: null,
    companyId: null,
    role: null,
    shopUrl: null,
    plan: null,
    trialStatus: null,
    superAdmin: false,
    superAdminGlobal: false,
    superAdminTenants: [],
    adminCompanies: [],
    loading: true,
    status: null,
    error: null,
    setCompanyId: () => { },
    applySelectedCompany: () => { },
};

type InternalState = Omit<UserState, "setCompanyId" | "applySelectedCompany">;

const defaultInternalState: InternalState = {
    uid: null,
    tenantId: null,
    companyId: null,
    role: null,
    shopUrl: null,
    plan: null,
    trialStatus: null,
    superAdmin: false,
    superAdminGlobal: false,
    superAdminTenants: [],
    adminCompanies: [],
    loading: true,
    status: null,
    error: null,
};

const UserContext = createContext<UserState>(defaultState);

function isDemoPath() {
    return typeof window !== "undefined" && window.location.pathname.startsWith("/demo");
}

function asStringArray(v: any): string[] {
    if (!Array.isArray(v)) return [];
    return v.map((x) => String(x ?? "").trim()).filter(Boolean);
}

function safeStr(v: any): string | null {
    const s = String(v ?? "").trim();
    return s ? s : null;
}

function syncUrlParamsToStorageIfAny() {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const tenantId = sp.get("tenantId");
    const companyId = sp.get("companyId");
    const shopUrl = sp.get("shopUrl");

    if (tenantId) {
        localStorage.setItem("selectedTenant", tenantId);
        sessionStorage.setItem("login_tenant", tenantId);
    }
    if (companyId) {
        localStorage.setItem("companyId", companyId);
        localStorage.setItem("selectedCompanyId", companyId);
        localStorage.setItem("accountId", companyId);
    }
    if (shopUrl) {
        localStorage.setItem("shopUrl", shopUrl);
        localStorage.setItem("selectedShopUrl", shopUrl);
    }
}

function getStoredSelectedShopUrl(): string | null {
    if (typeof window === "undefined") return null;
    return safeStr(localStorage.getItem("selectedShopUrl") || localStorage.getItem("shopUrl") || "");
}

function syncPlanToLocalStorage(planRaw: any, trialRaw: any, notify = false) {
    if (typeof window === "undefined") return;

    const plan = String(planRaw ?? "").trim();
    const trial = String(trialRaw ?? "").trim();

    if (plan) localStorage.setItem("plan", plan);
    else localStorage.removeItem("plan");

    if (trial) localStorage.setItem("trialStatus", trial);
    else localStorage.removeItem("trialStatus");

    if (notify) {
        window.dispatchEvent(new Event("company-selection-changed"));
    }
}

function sleep(ms: number) {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

// ✅ 追加: ECX token が localStorage に現れるまで少し待つ
async function waitForEcxToken(maxWaitMs = 3000, intervalMs = 150): Promise<string | null> {
    if (typeof window === "undefined") return null;

    const started = Date.now();
    while (Date.now() - started < maxWaitMs) {
        const token = String(localStorage.getItem("ecx_access_token") || "").trim();
        if (token) return token;
        await sleep(intervalMs);
    }
    return null;
}

export function UserProvider({ children }: { children: React.ReactNode }) {

    const applySelectedCompany = useCallback((payload: {
        tenantId?: string | null;
        companyId?: string | null;
        shopUrl?: string | null;
        plan?: string | null;
        trialStatus?: string | null;
    }) => {
        const nextTenantId = safeStr(payload.tenantId);
        const nextCompanyId = safeStr(payload.companyId);
        const nextShopUrl = safeStr(payload.shopUrl);
        const nextPlan = safeStr(payload.plan);
        const nextTrialStatus = safeStr(payload.trialStatus);

        if (nextTenantId) {
            localStorage.setItem("selectedTenant", nextTenantId);
            sessionStorage.setItem("login_tenant", nextTenantId);
        }

        saveCompanyId(nextCompanyId);
        setSelectedCompanyId(nextCompanyId);

        if (typeof window !== "undefined") {
            if (nextShopUrl) {
                localStorage.setItem("shopUrl", nextShopUrl);
                localStorage.setItem("selectedShopUrl", nextShopUrl);
            } else {
                localStorage.removeItem("shopUrl");
                localStorage.removeItem("selectedShopUrl");
            }

            if (nextPlan) localStorage.setItem("plan", nextPlan);
            else localStorage.removeItem("plan");

            if (nextTrialStatus) localStorage.setItem("trialStatus", nextTrialStatus);
            else localStorage.removeItem("trialStatus");
        }

        setState((s) => ({
            ...s,
            tenantId: nextTenantId ?? s.tenantId,
            companyId: nextCompanyId,
            shopUrl: nextShopUrl,
            plan: nextPlan,
            trialStatus: nextTrialStatus,
        }));
    }, []);

    const [state, setState] = useState<InternalState>(() => ({
        ...defaultInternalState,
        companyId: getCompanyId(),
        loading: true,
    }));

    const setCompanyId = useCallback((id: string | null) => {
        saveCompanyId(id);
        setSelectedCompanyId(id);
        setState((s) => ({ ...s, companyId: id }));
    }, []);

    const fetchEcxMeCompany = useCallback(async (token: string): Promise<Response> => {
        const buildInit = (t: string): RequestInit => ({
            method: "GET",
            cache: "no-store",
            headers: {
                Authorization: `Bearer ${t}`,
                "Cache-Control": "no-store",
                Pragma: "no-cache",
            },
        });

        const fetchOnce = async (t: string) => {
            const url = `/api/ecx/me/company?ts=${Date.now()}`;
            let r = await fetch(url, buildInit(t));
            if (r.status === 304) {
                r = await fetch(`/api/ecx/me/company?ts=${Date.now()}`, buildInit(t));
            }
            return r;
        };

        let res = await fetchOnce(token);

        // 初回ログイン直後の token 反映レース対策
        if (res.status === 401) {
            await sleep(400);
            const token2 = String(localStorage.getItem("ecx_access_token") || "").trim();
            if (token2) {
                res = await fetchOnce(token2);
            }
        }

        return res;
    }, []);

    const fetchMeFallback = useCallback(async () => {
        try {
            const r = await apiGet("/api/me");
            if (!r.ok) return null;
            const j = await r.json().catch(() => null);
            if (!j?.ok) return null;
            return j as any;
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        if (isDemoPath()) {
            const demoCompanyId = "demo-company";
            const demoShopUrl = "demo-shop";

            localStorage.setItem("selectedTenant", "demo");
            saveCompanyId(demoCompanyId);
            setSelectedCompanyId(demoCompanyId);
            localStorage.setItem("plan", "demo");
            localStorage.setItem("trialStatus", "active");

            setState({
                uid: "demo",
                tenantId: "demo",
                companyId: demoCompanyId,
                role: "admin",
                shopUrl: demoShopUrl,
                plan: "demo",
                trialStatus: "active",
                superAdmin: false,
                superAdminGlobal: false,
                superAdminTenants: [],
                adminCompanies: [],
                status: "approved",
                loading: false,
                error: null,
            });
            return;
        }

        const auth = getAuth();

        const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
            if (!firebaseUser) {
                clearCompanyContext();
                setState({ ...defaultInternalState, loading: false });

                if (typeof window !== "undefined") {
                    localStorage.removeItem("plan");
                    localStorage.removeItem("trialStatus");
                    //window.dispatchEvent(new Event("company-selection-changed"));
                }
                return;
            }

            try {
                syncUrlParamsToStorageIfAny();

                const storedTenant = String(localStorage.getItem("selectedTenant") || "").trim();
                const initialEcxToken = localStorage.getItem("ecx_access_token");
                const tenantFromPath =
                    typeof window !== "undefined" && window.location.pathname.startsWith("/ecx")
                        ? "ecx"
                        : "";
                const selectedTenant = storedTenant || tenantFromPath || "ecvision";

                console.log("[UserContext] selectedTenant=", selectedTenant);
                console.log("[UserContext] hasEcxToken=", !!initialEcxToken);

                const isSuperImpersonation = !!localStorage.getItem("superImpersonateCompanyId");

                // ECX OAuthコールバック中かどうか（ecx_code_verifier は cleanup() 前まで残る）
                const isEcxCallbackFlow = !!sessionStorage.getItem("ecx_code_verifier");

                let res: Response;

                // ECXフローに入る条件:
                //   1. スーパーインパーソネーション中でない
                //   2. selectedTenant === "ecx"
                //   3. ECXトークンが既にある OR ECX OAuthコールバックの最中（token未保存の初回レース対策）
                // ※ 条件3がないと、superAdminが selectedTenant="ecx" を持っているだけで
                //   3秒待機 → error:"unauthorized" になってしまう
                if (!isSuperImpersonation && selectedTenant === "ecx" && (initialEcxToken || isEcxCallbackFlow)) {
                    // ✅ ここが本丸: token 未保存の初回レースを待つ
                    const token = (await waitForEcxToken(3000, 150)) || "";

                    console.log("[UserContext] hasEcxToken(after wait)=", !!token);

                    if (!token) {
                        // すぐ unauthorized にせず、読み込み完了だけ落とす
                        setState((prev) => ({
                            ...prev,
                            uid: firebaseUser.uid,
                            tenantId: "ecx",
                            loading: false,
                            error: "unauthorized",
                        }));
                        return;
                    }

                    res = await fetchEcxMeCompany(token);
                } else {
                    console.log("[UserContext] calling /api/me/company", { uid: firebaseUser.uid, selectedTenant, stack: new Error().stack });
                    res = await apiGet("/api/me/company");
                }

                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));

                    // /api/me/company が account未登録で落ちても、super情報は /api/me で救済する
                    if (String(err?.error || "") === "Account not found") {
                        const me = await fetchMeFallback();
                        if (me) {
                            const fallbackSuperAdmin = !!me.superAdmin;
                            const fallbackSuperAdminGlobal = !!me.superAdminGlobal;
                            const fallbackSuperTenants = asStringArray(me.superAdminTenants);
                            const fallbackIsSuper =
                                fallbackSuperAdmin ||
                                fallbackSuperAdminGlobal ||
                                fallbackSuperTenants.length > 0;

                            if (fallbackIsSuper) {
                                // テナント専用superの場合、selectedTenantを許可テナントに切り替える
                                const effectiveTenant: string =
                                    fallbackSuperAdminGlobal
                                        ? selectedTenant
                                        : (fallbackSuperTenants[0] || selectedTenant);

                                if (!fallbackSuperAdminGlobal && fallbackSuperTenants.length > 0 && effectiveTenant !== selectedTenant) {
                                    localStorage.setItem("selectedTenant", effectiveTenant);
                                    sessionStorage.setItem("login_tenant", effectiveTenant);
                                }

                                setState({
                                    uid: firebaseUser.uid,
                                    tenantId: effectiveTenant,
                                    companyId: null,
                                    role: me.role ?? null,
                                    shopUrl: null,
                                    plan: null,
                                    trialStatus: null,
                                    superAdmin: fallbackSuperAdmin || fallbackSuperTenants.length > 0,
                                    superAdminGlobal: fallbackSuperAdminGlobal,
                                    superAdminTenants: fallbackSuperTenants,
                                    adminCompanies: Array.isArray(me.adminCompanies) ? me.adminCompanies : [],
                                    status: (me.status ?? null) as any,
                                    loading: false,
                                    error: null,
                                });
                                return;
                            }
                        }
                    }

                    if (res.status === 403 && err?.error === "tenant_mismatch") {
                        clearCompanyContext();
                        setState({
                            ...defaultInternalState,
                            uid: firebaseUser.uid,
                            loading: false,
                            error: "tenant_mismatch",
                        });
                        return;
                    }

                    throw new Error(err?.error || `api_error_${res.status}`);
                }

                const json: any = await res.json();

                let superAdmin = !!json.superAdmin;
                let superAdminGlobal = typeof json.superAdminGlobal === "boolean" ? json.superAdminGlobal : false;
                let superAdminTenants = asStringArray(json.superAdminTenants);

                console.log("[UserContext] /api/me/company super(raw)", {
                    selectedTenant,
                    tenantId: json?.tenantId ?? null,
                    superAdmin: !!json?.superAdmin,
                    superAdminGlobal: typeof json?.superAdminGlobal === "boolean" ? json.superAdminGlobal : null,
                    superAdminTenants: asStringArray(json?.superAdminTenants),
                });

                if (superAdmin && typeof json.superAdminGlobal !== "boolean") {
                    const me = await fetchMeFallback();
                    if (me) {
                        superAdminGlobal = !!me.superAdminGlobal;
                        superAdminTenants = asStringArray(me.superAdminTenants);
                        superAdmin = !!me.superAdmin;
                    }
                }

                // ecx経路や判定が弱いケースでも /api/me のsuper情報で必ず補完する。
                if (selectedTenant === "ecx" || !superAdmin || (!superAdminGlobal && superAdminTenants.length === 0)) {
                    const me = await fetchMeFallback();
                    if (me) {
                        const meSuperAdmin = !!me.superAdmin;
                        const meSuperGlobal = !!me.superAdminGlobal;
                        const meTenants = asStringArray(me.superAdminTenants);

                        superAdmin = superAdmin || meSuperAdmin;
                        superAdminGlobal = superAdminGlobal || meSuperGlobal;
                        superAdminTenants = Array.from(new Set([...superAdminTenants, ...meTenants]));

                        console.log("[UserContext] /api/me super(fallback)", {
                            selectedTenant,
                            meSuperAdmin,
                            meSuperGlobal,
                            meTenants,
                            merged: {
                                superAdmin,
                                superAdminGlobal,
                                superAdminTenants,
                            },
                        });
                    }
                }

                const hasSuperAccess =
                    superAdmin ||
                    superAdminGlobal ||
                    (Array.isArray(superAdminTenants) && superAdminTenants.length > 0);

                console.log("[UserContext] super(merged)", {
                    selectedTenant,
                    hasSuperAccess,
                    superAdmin,
                    superAdminGlobal,
                    superAdminTenants,
                });

                const companies: AdminCompany[] = Array.isArray(json.adminCompanies) ? json.adminCompanies : [];
                const storedSelectedShopUrl = getStoredSelectedShopUrl();
                const storedCompanyId = getCompanyId();
                const storedSelectedId = getSelectedCompanyId();

                let activeCompanyId: string | null = null;
                let activeShopUrl: string | null = null;
                let activePlan: string | null = null;
                let activeTrial: string | null = null;

                if (hasSuperAccess) {
                    activeCompanyId = safeStr(storedSelectedId ?? storedCompanyId ?? json.companyId ?? null);

                    let activeRow: any =
                        (activeCompanyId && storedSelectedShopUrl)
                            ? companies.find((c) =>
                                String(c.companyId) === String(activeCompanyId) &&
                                String(c.shopUrl ?? "").trim() === String(storedSelectedShopUrl).trim()
                            )
                            : null;

                    if (!activeRow && activeCompanyId) {
                        activeRow = companies.find((c) => String(c.companyId) === String(activeCompanyId));
                    }

                    if (!activeRow) {
                        activeRow = companies[0] ?? null;
                    }

                    activeCompanyId = safeStr(activeRow?.companyId ?? null);
                    activeShopUrl = safeStr(activeRow?.shopUrl ?? null);
                    activePlan = safeStr(activeRow?.plan ?? json?.plan ?? null);
                    activeTrial = safeStr(activeRow?.trialStatus ?? json?.trialStatus ?? null);

                    saveCompanyId(activeCompanyId);
                    setSelectedCompanyId(activeCompanyId);

                    if (typeof window !== "undefined" && activeShopUrl) {
                        localStorage.setItem("shopUrl", activeShopUrl);
                        localStorage.setItem("selectedShopUrl", activeShopUrl);
                    }
                } else {
                    activeCompanyId = safeStr(json.companyId ?? storedCompanyId ?? null);
                    activeShopUrl = safeStr(json.shopUrl ?? storedSelectedShopUrl ?? null);
                    activePlan = safeStr(json.plan ?? null);
                    activeTrial = safeStr(json.trialStatus ?? null);

                    if (!json.companyId && !hasSuperAccess && selectedTenant !== "ecx") {
                        clearCompanyContext();
                        activeCompanyId = null;
                    }

                    saveCompanyId(activeCompanyId);

                    if (typeof window !== "undefined" && activeShopUrl) {
                        localStorage.setItem("shopUrl", activeShopUrl);
                        localStorage.setItem("selectedShopUrl", activeShopUrl);
                    }
                }

                syncPlanToLocalStorage(activePlan, activeTrial);

                // バックエンドがテナントを自動切替した場合（ECXテナント専用スーパーユーザーが
                // ecvisionホスト経由でアクセスした場合など）、localStorageを正しいテナントに更新する
                const responseTenantId = typeof json.tenantId === "string" ? json.tenantId : null;
                if (responseTenantId && responseTenantId !== selectedTenant) {
                    localStorage.setItem("selectedTenant", responseTenantId);
                    sessionStorage.setItem("login_tenant", responseTenantId);
                }

                setState({
                    uid: firebaseUser.uid,
                    tenantId: json.tenantId ?? (selectedTenant === "ecx" ? "ecx" : null),
                    companyId: activeCompanyId,
                    role: json.role ?? null,
                    shopUrl: activeShopUrl,
                    plan: activePlan,
                    trialStatus: activeTrial,
                    superAdmin,
                    superAdminGlobal,
                    superAdminTenants,
                    adminCompanies: companies,
                    status: (json.status ?? null) as any,
                    loading: false,
                    error: null,
                });
            } catch (e) {
                console.error("UserContext init error:", e);
                clearCompanyContext();
                setState({
                    ...defaultInternalState,
                    uid: firebaseUser.uid,
                    loading: false,
                    error: "unauthorized",
                });

                if (typeof window !== "undefined") {
                    localStorage.removeItem("plan");
                    localStorage.removeItem("trialStatus");
                    //window.dispatchEvent(new Event("company-selection-changed"));
                }
            }
        });

        return () => unsub();
    }, [setCompanyId, fetchEcxMeCompany, fetchMeFallback]);

    return (
        <UserContext.Provider value={{ ...state, setCompanyId, applySelectedCompany }}>
            {children}
        </UserContext.Provider>
    );
}

export function useUserContext() {
    return useContext(UserContext);
}